import * as React from 'react';
import { View, StyleSheet, ViewStyle, TextStyle, Text } from 'react-native';
import zxcvbn from 'zxcvbn';

import { getLocalizedTexts } from '../../Locales';
import HighlightedText from '../common/HighlightedText';
import { font } from '../../style/text';
import color from '../../style/color';

const PASSWORD_MIN_CHARACTERS = 10;
const PASSWORD_MAX_CHARACTERS = 50;
const PASSWORD_MIN_LOWER_CASE = 1;
const PASSWORD_MIN_UPPER_CASE = 1;
const PASSWORD_MIN_NUMBERS = 1;
// zxcvbn rates strength from 0 (weakest) to 4 (strongest). We only reject the
// genuinely weak scores (0 and 1), keeping the rule light: a 10-char password
// with mixed case + digits that is not a common/guessable one already scores >= 2.
const PASSWORD_MIN_STRENGTH_SCORE = 2;

/**
 * The reason to surface once the composition checklist is satisfied but the
 * password is still rejected for being too weak. Returns `null` while the
 * checklist is incomplete or the password is fully valid, so we never nag the
 * user mid-typing.
 */
function getRejectionReasonKey(checks: PasswordRequirementsChecks): PasswordStrengthWarning | null {
    const compositionMet = checks.minCharacters && checks.minLowerCase && checks.minUpperCase && checks.minNumbers;
    if (!compositionMet || checks.all) return null;
    return checks.strengthWarning;
}

const PasswordRequirements = ({
    passwordRequirementsChecks,
    style,
    isInline,
}: {
    passwordRequirementsChecks: PasswordRequirementsChecks;
    style?: TextStyle & ViewStyle;
    isInline?: boolean;
}) => {
    const texts = getLocalizedTexts().auth.signUp.withEmail.passwordRequirements;
    const reasonKey = getRejectionReasonKey(passwordRequirementsChecks);
    const Requirements = () => (
        <>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minCharacters}>
                {texts.minCharacters({ minCharacters: PASSWORD_MIN_CHARACTERS })}
            </PasswordRequirementLine>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minLowerCase}>
                {texts.minLowerCase({ minLowerCase: PASSWORD_MIN_LOWER_CASE })}
            </PasswordRequirementLine>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minUpperCase}>
                {texts.minUpperCase({ minUpperCase: PASSWORD_MIN_UPPER_CASE })}
            </PasswordRequirementLine>
            <PasswordRequirementLine isChecked={passwordRequirementsChecks.minNumbers}>
                {texts.minNumbers({ minNumbers: PASSWORD_MIN_NUMBERS })}
            </PasswordRequirementLine>
            {reasonKey ? <Text style={styles.textPasswordReason}>{` ${texts.reason[reasonKey]}`}</Text> : null}
        </>
    );
    if (isInline)
        return (
            <Text style={style}>
                <Requirements />
            </Text>
        );
    else
        return (
            <View style={[styles.containerPasswordRequirements, style]}>
                <Requirements />
            </View>
        );
};

export default PasswordRequirements;

const PasswordRequirementLine = ({ isChecked, children }: { isChecked: boolean; children: string }) => (
    <HighlightedText
        style={styles.textPasswordRequirement}
        highlightedStyle={{
            color: isChecked ? color.caribbeanGreen : color.manatee,
            fontFamily: font.ambitBold,
        }}>
        {children}
    </HighlightedText>
);

/**
 * Why a password was judged too weak, so the UI can explain the rejection
 * instead of only saying "not strong enough". Mapped from zxcvbn's feedback.
 */
export type PasswordStrengthWarning =
    | 'commonPassword' // a common / previously-leaked password
    | 'sequenceOrPattern' // keyboard rows or sequences like "abc", "6543"
    | 'repeats' // repeats like "aaa" or "abcabc"
    | 'predictableWord' // a single word, name, surname, date or year
    | 'tooWeak'; // generic fallback

export interface PasswordRequirementsChecks {
    minCharacters: boolean;
    minLowerCase: boolean;
    minUpperCase: boolean;
    minNumbers: boolean;
    strongEnough: boolean;
    /** Reason the password is too weak, or `null` when it is strong enough. */
    strengthWarning: PasswordStrengthWarning | null;
    all: boolean;
}

/** Maps a zxcvbn English warning into one of our stable, localizable reason codes. */
function toStrengthWarning(zxcvbnWarning: string): PasswordStrengthWarning {
    const warning = zxcvbnWarning.toLowerCase();
    if (warning.includes('common password') || warning.includes('commonly used')) return 'commonPassword';
    if (warning.includes('repeat')) return 'repeats';
    if (warning.includes('sequence') || warning.includes('keys') || warning.includes('keyboard'))
        return 'sequenceOrPattern';
    if (warning.includes('word') || warning.includes('name') || warning.includes('date') || warning.includes('year'))
        return 'predictableWord';
    return 'tooWeak';
}

export function checkPasswordRequirements(password: string): PasswordRequirementsChecks {
    const minCharacters = password.length >= PASSWORD_MIN_CHARACTERS && password.length <= PASSWORD_MAX_CHARACTERS;
    const minLowerCase = password.length - password.replace(/[a-z]/g, '').length >= PASSWORD_MIN_LOWER_CASE;
    const minUpperCase = password.length - password.replace(/[A-Z]/g, '').length >= PASSWORD_MIN_UPPER_CASE;
    const minNumbers = password.length - password.replace(/[0-9]/g, '').length >= PASSWORD_MIN_NUMBERS;
    const { score, feedback } = zxcvbn(password);
    const strongEnough = score >= PASSWORD_MIN_STRENGTH_SCORE;
    const strengthWarning = strongEnough ? null : toStrengthWarning(feedback.warning || '');
    const checks: PasswordRequirementsChecks = {
        minCharacters,
        minLowerCase,
        minUpperCase,
        minNumbers,
        strongEnough,
        strengthWarning,
        all: minCharacters && minLowerCase && minUpperCase && minNumbers && strongEnough,
    };
    return checks;
}

export function usePasswordRequirementsChecks(password: string): PasswordRequirementsChecks {
    const [passwordRequirementsChecks, setPasswordRequirementsChecks] = React.useState<PasswordRequirementsChecks>(
        checkPasswordRequirements(password)
    );
    React.useEffect(() => {
        setPasswordRequirementsChecks(checkPasswordRequirements(password));
    }, [password]);
    return passwordRequirementsChecks;
}

const styles = StyleSheet.create({
    containerPasswordRequirements: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignSelf: 'flex-start',
    },
    textPasswordRequirement: {
        color: color.frenchGray,
        fontFamily: font.ambitSemiBold,
        fontSize: 12,
    },
    textPasswordReason: {
        color: color.flamingo,
        fontFamily: font.ambitSemiBold,
        fontSize: 12,
    },
});
